import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFantasyPlayers } from '../hooks/useFantasyData';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorAlert from '../components/common/ErrorAlert';
import '../styles/FantasyPlayers.css';

const FantasyPlayersPage = ({ setActiveTab }) => {
  const { t } = useTranslation();
  const [season] = useState(2024);
  const [selectedRole, setSelectedRole] = useState('');
  const [sortBy, setSortBy] = useState('name');

  const { players, loading, error } = useFantasyPlayers(season);

  const filteredPlayers = selectedRole
    ? players.filter((p) => p.role === selectedRole)
    : players;

  const sortedPlayers = [...filteredPlayers].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'team':
        return (a.team?.name || '').localeCompare(b.team?.name || '');
      case 'role':
        return a.role.localeCompare(b.role);
      case 'goals':
        return (b.goalsScored || 0) - (a.goalsScored || 0);
      case 'assists':
        return (b.assists || 0) - (a.assists || 0);
      default:
        return 0;
    }
  });

  const roleOptions = ['P', 'D', 'C', 'A'];
  const roleLabels = {
    P: t('fantasy.role.goalkeeper'),
    D: t('fantasy.role.defender'),
    C: t('fantasy.role.midfielder'),
    A: t('fantasy.role.attacker')
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorAlert message={error} />;

  return (
    <div className="fantasy-players-page container mt-4">
      <button
        className="btn-back-header mb-3"
        onClick={() => setActiveTab(9)}
        aria-label="Back to Fantasy main"
        title="Back to Fantasy"
      >
        ← Back
      </button>
      <h1 className="mb-4">{t('fantasy.players.title')}</h1>

      <div className="row mb-4">
        <div className="col-md-6">
          <div className="form-group">
            <label htmlFor="roleFilter">{t('fantasy.filters.role')}</label>
            <select
              id="roleFilter"
              className="form-control"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              <option value="">{t('fantasy.filters.all_roles')}</option>
              {roleOptions.map((role) => (
                <option key={role} value={role}>
                  {roleLabels[role]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="col-md-6">
          <div className="form-group">
            <label htmlFor="sortBy">{t('fantasy.filters.sort_by')}</label>
            <select
              id="sortBy"
              className="form-control"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="name">{t('fantasy.filters.name')}</option>
              <option value="team">{t('fantasy.filters.team')}</option>
              <option value="role">{t('fantasy.filters.role')}</option>
              <option value="goals">{t('fantasy.filters.goals')}</option>
              <option value="assists">{t('fantasy.filters.assists')}</option>
            </select>
          </div>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead className="table-dark">
            <tr>
              <th>{t('fantasy.players.name')}</th>
              <th>{t('fantasy.players.role')}</th>
              <th>{t('fantasy.players.team')}</th>
              <th>{t('fantasy.players.appearances')}</th>
              <th>{t('fantasy.players.goals')}</th>
              <th>{t('fantasy.players.assists')}</th>
              <th>{t('fantasy.players.quotation')}</th>
            </tr>
          </thead>
          <tbody>
            {sortedPlayers.map((player) => (
              <tr key={player.id}>
                <td className="player-name">{player.name}</td>
                <td>
                  <span className={`role-badge role-${player.role}`}>
                    {roleLabels[player.role]}
                  </span>
                </td>
                <td>{player.team?.name || 'N/A'}</td>
                <td>{player.appearances || 0}</td>
                <td>{player.goalsScored || 0}</td>
                <td>{player.assists || 0}</td>
                <td className="quotation">
                  {player.quotationCurrent ? `€${player.quotationCurrent}` : 'N/A'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="results-info mt-3">
        <p className="text-muted">
          {t('fantasy.players.total')}: <strong>{sortedPlayers.length}</strong>
        </p>
      </div>
    </div>
  );
};

export default FantasyPlayersPage;
